import React from 'react'
import { useApp } from '../context/AppContext'
import { Btn, Badge, TableWrap } from '../shared/components'
import { useT } from '../shared'
import { useOrgName } from '../shared/BrandLogo'
import {
  listCertBatches,
  createCertBatch,
  getCertBatch,
  updateCertBatch,
  deleteCertBatch,
  addEligibleRecipients,
  importRecipients,
  removeRecipient,
  issueCertBatch,
  listAllRecipients,
  getEmailTemplate,
  updateEmailTemplate,
  resetEmailTemplate,
  listCollections,
  createCollection,
  updateCollection,
  deleteCollection,
} from '../api/certBatch.api'
import { listProgramTrainers, listClasses } from '../api/programs.api'

// ─── helpers ──────────────────────────────────────────────────────────────────

const TIMEZONES = [
  'Asia/Jakarta',
  'Asia/Makassar',
  'Asia/Jayapura',
  'UTC',
]

function statusBadge(status) {
  if (status === 'DRAFT') return <Badge tone="default">Draft</Badge>
  if (status === 'ACTIVE') return <Badge tone="warning">Active</Badge>
  if (status === 'COMPLETED') return <Badge tone="success">Completed</Badge>
  return null
}

function recipientStatusBadge(status) {
  if (status === 'REQUESTED') return <Badge tone="default">Requested</Badge>
  if (status === 'CONFIRMED') return <Badge tone="warning">Confirmed</Badge>
  if (status === 'SENT') return <Badge tone="success">Sent</Badge>
  return null
}

function fmtDate(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
}

function fmtDatetime(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/**
 * Nama & email penerima, dari jalur mana pun ia datang.
 *
 * Penerima datang lewat dua jalur: peserta terdaftar (punya enrollment dan
 * akun), atau nama+email yang diunggah admin lewat Excel untuk seminar sekali
 * jalan. Membacanya langsung dari r.enrollment.user hanya menampilkan "—" untuk
 * jalur kedua. Padanan sisi server ada di certIdentity.service.js.
 */
function namaPenerima(r) {
  return r?.enrollment?.user?.full_name ?? r?.recipient_name ?? '—'
}

function emailPenerima(r) {
  return r?.enrollment?.user?.email ?? r?.recipient_email ?? '—'
}

/**
 * Salin alamat kredensial sebuah sertifikat.
 *
 * Yang disalin adalah /c/<public_id> — halaman yang dirender server dan punya
 * pratinjau Open Graph, jadi saat admin menempelkannya ke peserta lewat
 * WhatsApp, yang muncul kartu bergambar sertifikatnya, bukan teks polos.
 *
 * ?verify= lama tetap dipakai untuk baris yang belum punya public_id: sertifikat
 * yang terbit sebelum kolom itu ada baru dibuatkan alamatnya saat pertama kali
 * dibuka, dan tautan lama memang tidak pernah dimatikan.
 */
function copyVerifyUrl(cert) {
  const url = cert?.public_id
    ? `${window.location.origin}/c/${cert.public_id}`
    : `${window.location.origin}/?verify=${encodeURIComponent(cert?.cert_no ?? '')}`
  navigator.clipboard.writeText(url).catch(() => {})
}

// ─── Training Detail Form Modal ───────────────────────────────────────────────

function BatchFormModal({ batch, programId, trainers, collections = [], classes = [], onClose, onSaved }) {
  const { lang } = useApp()
  const id = lang === 'id'

  const [name, setName] = React.useState(batch?.name ?? '')
  const [classId, setClassId] = React.useState(batch?.class_id ?? '')
  const [collectionId, setCollectionId] = React.useState(batch?.collection_id ?? '')
  const [trainingDate, setTrainingDate] = React.useState(
    batch?.training_date ? new Date(batch.training_date).toISOString().slice(0, 16) : ''
  )
  const [timezone, setTimezone] = React.useState(batch?.timezone ?? 'Asia/Jakarta')
  const [trainerId, setTrainerId] = React.useState(batch?.trainer_id ?? '')
  const [deliveryType, setDeliveryType] = React.useState(batch?.delivery_type ?? 'TO_RECIPIENT')
  const [customEmails, setCustomEmails] = React.useState((batch?.custom_emails ?? []).join(', '))
  const [useTrainingDate, setUseTrainingDate] = React.useState(batch?.use_training_date_as_issue ?? false)
  const [validityMonths, setValidityMonths] = React.useState(batch?.cert_validity_months ?? 60)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')

  async function handleSave() {
    if (!name.trim()) { setError(id ? 'Nama wajib diisi.' : 'Name is required.'); return }
    setSaving(true)
    setError('')
    try {
      const payload = {
        name: name.trim(),
        class_id: classId || null,
        training_date: trainingDate ? new Date(trainingDate).toISOString() : null,
        timezone,
        trainer_id: trainerId || null,
        delivery_type: deliveryType,
        custom_emails: deliveryType === 'TO_CUSTOM_EMAIL'
          ? customEmails.split(',').map((e) => e.trim()).filter(Boolean)
          : [],
        use_training_date_as_issue: useTrainingDate,
        cert_validity_months: Number(validityMonths),
        collection_id: collectionId || null,
      }
      const result = batch
        ? await updateCertBatch(batch.id, payload)
        : await createCertBatch(programId, payload)
      onSaved(result)
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{id ? (batch ? 'Edit Training' : 'Buat Training') : (batch ? 'Edit Training' : 'Create Training')}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div className="alert alert-danger" style={{ padding: '8px 12px', fontSize: 13 }}>{error}</div>}

          <label className="field">
            <span className="field-label">{id ? 'Nama Training' : 'Training Name'} *</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder={id ? 'Nama internal (tidak dilihat peserta)' : 'Internal name (not shown to recipients)'} />
          </label>

          {classes.length > 0 && (
            <label className="field">
              <span className="field-label">{id ? 'Kelas' : 'Class'}</span>
              <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)}>
                <option value="">{id ? '— Tanpa kelas (lintas angkatan) —' : '— No class (cross-cohort) —'}</option>
                {classes.map((k) => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </select>
              <span className="tiny muted" style={{ marginTop: 4 }}>
                {id
                  ? 'Menautkan ke kelas membuat "Tambah peserta eligible" hanya mengambil peserta kelas ini.'
                  : 'Linking a class makes "Add eligible participants" pull only that class’s participants.'}
              </span>
            </label>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label className="field">
              <span className="field-label">{id ? 'Tanggal Training' : 'Training Date'}</span>
              <input className="input" type="datetime-local" value={trainingDate}
                onChange={(e) => setTrainingDate(e.target.value)} />
            </label>
            <label className="field">
              <span className="field-label">Timezone</span>
              <select className="input" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </label>
          </div>

          <label className="field">
            <span className="field-label">{id ? 'Trainer' : 'Trainer Selection'}</span>
            <select className="input" value={trainerId} onChange={(e) => setTrainerId(e.target.value)}>
              <option value="">{id ? '— Pilih trainer —' : '— Select trainer —'}</option>
              {trainers.map((t) => (
                <option key={t.user.id} value={t.user.id}>{t.user.full_name}</option>
              ))}
            </select>
          </label>

          {collections.length > 0 && (
            <label className="field">
              <span className="field-label">{id ? 'Koleksi' : 'Collection'}</span>
              <select className="input" value={collectionId} onChange={(e) => setCollectionId(e.target.value)}>
                <option value="">{id ? '— Tanpa koleksi —' : '— No collection —'}</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
          )}

          <fieldset style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
            <legend style={{ fontSize: 12, fontWeight: 600, padding: '0 4px', color: 'var(--fg-muted)' }}>
              {id ? 'Tipe Pengiriman Kredensial' : 'Credential Delivery Type'}
            </legend>
            {[
              { val: 'TO_RECIPIENT', label: id ? 'Kirim ke peserta' : 'Send to recipient' },
              { val: 'TO_TRAINER', label: id ? 'Kirim ke trainer' : 'Send to trainer' },
              { val: 'TO_CUSTOM_EMAIL', label: id ? 'Kirim ke email kustom' : 'Send to custom email addresses' },
            ].map(({ val, label }) => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                <input type="radio" name="delivery_type" value={val}
                  checked={deliveryType === val} onChange={() => setDeliveryType(val)} />
                <span style={{ fontSize: 13 }}>{label}</span>
              </label>
            ))}
            {deliveryType === 'TO_CUSTOM_EMAIL' && (
              <div style={{ marginTop: 8 }}>
                <label className="field">
                  <span className="field-label">{id ? 'Email kustom (pisahkan dengan koma)' : 'Custom emails (comma-separated)'}</span>
                  <textarea className="input" rows={2} value={customEmails}
                    onChange={(e) => setCustomEmails(e.target.value)}
                    placeholder="email1@example.com, email2@example.com" />
                </label>
              </div>
            )}
          </fieldset>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={useTrainingDate} onChange={(e) => setUseTrainingDate(e.target.checked)} />
            <span style={{ fontSize: 13 }}>
              {id ? 'Gunakan tanggal training sebagai tanggal terbit sertifikat' : 'Use training date as certificate issue date'}
            </span>
          </label>

          <label className="field">
            <span className="field-label">{id ? 'Masa berlaku sertifikat' : 'Certificate validity'}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select className="input" style={{ width: 160 }} value={validityMonths} onChange={(e) => setValidityMonths(e.target.value)}>
                <option value={0}>{id ? 'Tidak ada batas' : 'No expiry'}</option>
                <option value={12}>{id ? '1 tahun' : '1 year'}</option>
                <option value={24}>{id ? '2 tahun' : '2 years'}</option>
                <option value={36}>{id ? '3 tahun' : '3 years'}</option>
                <option value={60}>{id ? '5 tahun' : '5 years'}</option>
                <option value={120}>{id ? '10 tahun' : '10 years'}</option>
              </select>
            </div>
          </label>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="secondary" onClick={onClose} disabled={saving}>{id ? 'Batal' : 'Cancel'}</Btn>
          <Btn variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (id ? 'Menyimpan…' : 'Saving…') : (id ? 'Simpan' : 'Save')}
          </Btn>
        </div>
      </div>
    </div>
  )
}

// ─── Batch Detail View ────────────────────────────────────────────────────────

function BatchDetail({ batchId, programId, trainers, collections = [], classes = [], onBack, onDeleted }) {
  const { lang } = useApp()
  const id = lang === 'id'
  const [batch, setBatch] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [editing, setEditing] = React.useState(false)
  const [issuing, setIssuing] = React.useState(false)
  const [addingEligible, setAddingEligible] = React.useState(false)
  const [issueResult, setIssueResult] = React.useState(null)
  const [error, setError] = React.useState('')
  // Impor penerima lepas — untuk seminar/webinar yang pesertanya tidak
  // terdaftar di LMS.
  const [importing, setImporting] = React.useState(false)
  const [importResult, setImportResult] = React.useState(null)
  const fileRef = React.useRef(null)

  const load = React.useCallback(async () => {
    try {
      setLoading(true)
      setBatch(await getCertBatch(batchId))
    } finally {
      setLoading(false)
    }
  }, [batchId])

  React.useEffect(() => { load() }, [load])

  async function handleAddEligible() {
    setAddingEligible(true)
    setError('')
    try {
      const r = await addEligibleRecipients(batchId)
      await load()
      if (r.added === 0) setError(id ? `Tidak ada peserta eligible baru (${r.skipped} sudah ada).` : `No new eligible recipients (${r.skipped} already added).`)
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setAddingEligible(false)
    }
  }

  async function handleImportFile(file) {
    if (!file) return
    setImporting(true)
    setError('')
    setImportResult(null)
    try {
      const r = await importRecipients(batchId, file)
      setImportResult(r)
      await load()
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setImporting(false)
      // Dikosongkan supaya berkas yang sama bisa dipilih lagi setelah diperbaiki
      // — tanpa ini, memilih berkas yang identik tidak memicu onChange.
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function handleRemoveRecipient(rid) {
    if (!window.confirm(id ? 'Hapus penerima ini?' : 'Remove this recipient?')) return
    try {
      await removeRecipient(batchId, rid)
      await load()
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    }
  }

  async function handleIssue() {
    if (!window.confirm(id ? 'Issue sertifikat dan kirim email ke semua penerima berstatus REQUESTED?' : 'Issue certificates and send emails to all REQUESTED recipients?')) return
    setIssuing(true)
    setError('')
    setIssueResult(null)
    try {
      const r = await issueCertBatch(batchId)
      setIssueResult(r)
      await load()
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setIssuing(false)
    }
  }

  async function handleDelete() {
    if (!window.confirm(id ? 'Hapus batch ini?' : 'Delete this batch?')) return
    try {
      await deleteCertBatch(batchId)
      onDeleted()
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    }
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-muted)' }}>{id ? 'Memuat…' : 'Loading…'}</div>
  if (!batch) return null

  const requested = batch.recipients?.filter((r) => r.status === 'REQUESTED').length ?? 0

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← {id ? 'Kembali' : 'Back'}</button>
        <div style={{ flex: 1 }}>
          <div className="eyebrow" style={{ marginBottom: 2 }}>Training Detail</div>
          <h2 className="h2" style={{ margin: 0 }}>{batch.name}</h2>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {batch.status !== 'COMPLETED' && (
            <Btn variant="secondary" size="sm" onClick={() => setEditing(true)}>{id ? 'Edit' : 'Edit'}</Btn>
          )}
          {batch.status === 'DRAFT' && (
            <Btn variant="danger" size="sm" onClick={handleDelete}>{id ? 'Hapus' : 'Delete'}</Btn>
          )}
        </div>
      </div>

      {/* Info grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Status', val: statusBadge(batch.status) },
          { label: id ? 'Kelas' : 'Class', val: batch.class?.name || (id ? '— lintas angkatan —' : '— cross-cohort —') },
          { label: id ? 'Tanggal Training' : 'Training Date', val: fmtDate(batch.training_date) },
          { label: 'Timezone', val: batch.timezone },
          { label: 'Trainer', val: batch.trainer?.full_name ?? '—' },
          {
            label: id ? 'Tipe Pengiriman' : 'Delivery Type',
            val: batch.delivery_type === 'TO_RECIPIENT' ? (id ? 'Ke peserta' : 'To recipient')
              : batch.delivery_type === 'TO_TRAINER' ? (id ? 'Ke trainer' : 'To trainer')
              : (id ? 'Email kustom' : 'Custom email'),
          },
          { label: id ? 'Tanggal Issue' : 'Issue Date', val: batch.use_training_date_as_issue ? (id ? 'Ikut tanggal training' : 'Training date') : (id ? 'Waktu dikirim' : 'Send time') },
          { label: id ? 'Masa Berlaku' : 'Validity', val: batch.cert_validity_months > 0 ? `${batch.cert_validity_months} ${id ? 'bulan' : 'months'}` : (id ? 'Tanpa batas' : 'No expiry') },
          { label: id ? 'Koleksi' : 'Collection', val: batch.collection?.name ?? '—' },
        ].map(({ label, val }) => (
          <div key={label} style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{val}</div>
          </div>
        ))}
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 12, padding: '8px 12px', fontSize: 13 }}>{error}</div>}
      {issueResult && (
        <div className="alert alert-success" style={{ marginBottom: 12, padding: '8px 12px', fontSize: 13 }}>
          {issueResult.message}
          {issueResult.errors?.length > 0 && <div style={{ marginTop: 4 }}>{issueResult.errors.join(', ')}</div>}
        </div>
      )}

      {/* Hasil impor: baris yang gagal disebutkan satu per satu beserta nomor
          barisnya, supaya operator bisa langsung membuka Excel-nya dan
          memperbaiki baris itu saja — bukan mengunggah ulang semuanya. */}
      {importResult && (
        <div className={importResult.rejected > 0 ? 'alert alert-warning' : 'alert alert-success'}
             style={{ marginBottom: 12, padding: '10px 12px', fontSize: 13 }}>
          <div>
            {id
              ? `${importResult.added} penerima ditambahkan${importResult.rejected ? `, ${importResult.rejected} baris dilewati.` : '.'}`
              : `${importResult.added} recipients added${importResult.rejected ? `, ${importResult.rejected} rows skipped.` : '.'}`}
          </div>
          {importResult.errors?.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {importResult.errors.map((e, i) => (
                <li key={i}>{id ? 'Baris' : 'Row'} {e.row}: {e.error}</li>
              ))}
            </ul>
          )}
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, padding: '0 6px', fontSize: 12 }}
                  onClick={() => setImportResult(null)}>
            {id ? 'Tutup' : 'Dismiss'}
          </button>
        </div>
      )}

      {/* Recipients section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <h3 className="h3" style={{ margin: 0, flex: 1 }}>{id ? 'Penerima' : 'Recipients'} ({batch.recipients?.length ?? 0})</h3>
        {batch.status !== 'COMPLETED' && (
          <>
            <Btn variant="secondary" size="sm" onClick={handleAddEligible} disabled={addingEligible}>
              {addingEligible ? '…' : (id ? '+ Tambah Eligible' : '+ Add Eligible')}
            </Btn>
            {/* Penerima yang tidak terdaftar di LMS — seminar & webinar sekali
                jalan. Mereka melewati gerbang kelayakan; memang itu maksudnya. */}
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx"
              style={{ display: 'none' }}
              onChange={(e) => handleImportFile(e.target.files?.[0])}
            />
            <Btn variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={importing}
                 title={id ? 'Unggah XLSX/CSV berkolom full_name dan email' : 'Upload XLSX/CSV with full_name and email columns'}>
              {importing ? '…' : (id ? '+ Dari Excel' : '+ From Excel')}
            </Btn>
            {requested > 0 && (
              <Btn variant="primary" size="sm" onClick={handleIssue} disabled={issuing}>
                {issuing ? (id ? 'Mengirim…' : 'Sending…') : (id ? `Issue & Kirim (${requested})` : `Issue & Send (${requested})`)}
              </Btn>
            )}
          </>
        )}
      </div>

      {batch.recipients?.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--fg-muted)', fontSize: 14 }}>
          {id ? 'Belum ada penerima. Klik "+ Tambah Eligible" untuk menambahkan peserta yang memenuhi syarat.'
            : 'No recipients yet. Click "+ Add Eligible" to add eligible participants.'}
        </div>
      ) : (
        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>{id ? 'Nama' : 'Full Name'}</th>
                <th>Email</th>
                <th>{id ? 'No. Sertifikat' : 'Identification No.'}</th>
                <th>{id ? 'Berlaku Hingga' : 'Expiration Date'}</th>
                <th>{id ? 'Tgl Terbit' : 'Added On'}</th>
                <th>{id ? 'Status Email' : 'Email Status'}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {batch.recipients.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 500 }}>{namaPenerima(r)}</td>
                  <td style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{emailPenerima(r)}</td>
                  <td>
                    {r.certificate?.cert_no
                      ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {r.certificate.cert_no}
                          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 4, padding: '0 4px', fontSize: 11 }}
                            title={id ? 'Salin alamat kredensial' : 'Copy credential link'}
                            onClick={() => copyVerifyUrl(r.certificate)}>⎘</button>
                        </span>
                      : '—'}
                  </td>
                  <td style={{ fontSize: 13, color: r.certificate?.expires_at ? 'var(--fg)' : 'var(--fg-muted)' }}>
                    {r.certificate?.expires_at ? fmtDate(r.certificate.expires_at) : (id ? 'Tanpa batas' : 'No expiry')}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{fmtDatetime(r.certificate?.issued_at ?? r.created_at)}</td>
                  <td>{recipientStatusBadge(r.status)}</td>
                  <td>
                    {r.status === 'REQUESTED' && (
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                        onClick={() => handleRemoveRecipient(r.id)}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {editing && (
        <BatchFormModal
          batch={batch}
          programId={programId}
          trainers={trainers}
          collections={collections}
          classes={classes}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { setBatch((b) => ({ ...b, ...updated })); setEditing(false) }}
        />
      )}
    </div>
  )
}

// ─── Training Dashboard (list view) ──────────────────────────────────────────

function BatchTable({ batches, onSelect, emptyMsg }) {
  const { lang } = useApp()
  const id = lang === 'id'

  if (!batches?.length) return (
    <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--fg-muted)', fontSize: 14 }}>{emptyMsg}</div>
  )

  return (
    <TableWrap>
      <table>
        <thead>
          <tr>
            <th>{id ? 'Trainer' : 'Trainer'}</th>
            <th>Training</th>
            <th>{id ? 'Kelas' : 'Class'}</th>
            <th>{id ? 'Koleksi' : 'Collection'}</th>
            <th>{id ? 'Tgl Training' : 'Training Date'}</th>
            <th>{id ? 'Tgl Dibuat' : 'Added Date'}</th>
            <th>Status</th>
            <th>Requested</th>
            <th>Confirmed</th>
            <th>Sent</th>
          </tr>
        </thead>
        <tbody>
          {batches.map((b) => (
            <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => onSelect(b.id)}>
              <td style={{ fontSize: 13 }}>{b.trainer?.full_name ?? '—'}</td>
              <td style={{ fontWeight: 500 }}>{b.name}</td>
              <td style={{ fontSize: 13 }}>{b.class?.name ?? <span style={{ color: 'var(--fg-muted)' }}>—</span>}</td>
              <td style={{ fontSize: 13 }}>{b.collection ? <Badge tone="default">{b.collection.name}</Badge> : <span style={{ color: 'var(--fg-muted)' }}>—</span>}</td>
              <td style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{fmtDate(b.training_date)}</td>
              <td style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{fmtDate(b.created_at)}</td>
              <td>{statusBadge(b.status)}</td>
              <td style={{ textAlign: 'center' }}>{b.recipientSummary?.requested ?? 0}</td>
              <td style={{ textAlign: 'center' }}>{b.recipientSummary?.confirmed ?? 0}</td>
              <td style={{ textAlign: 'center' }}>{b.recipientSummary?.sent ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  )
}

// ─── Global Recipients View ───────────────────────────────────────────────────

function AllRecipientsView({ programId }) {
  const { lang } = useApp()
  const id = lang === 'id'
  const [data, setData] = React.useState(null)
  const [search, setSearch] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [query, setQuery] = React.useState('')

  const load = React.useCallback(async (q = '') => {
    setLoading(true)
    try {
      const res = await listAllRecipients(programId, { search: q, limit: 100 })
      setData(res)
    } finally {
      setLoading(false)
    }
  }, [programId])

  React.useEffect(() => { load() }, [load])

  function handleSearch(e) {
    e.preventDefault()
    load(query)
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h3 className="h3" style={{ margin: 0, flex: 1 }}>
          {id ? 'Semua Penerima' : 'All Recipients'}
          {data && <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--fg-muted)', marginLeft: 8 }}>({data.total})</span>}
        </h3>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
          <input className="input" style={{ width: 220, fontSize: 13 }}
            placeholder={id ? 'Cari nama, email, no. sertifikat…' : 'Search name, email, cert no…'}
            value={query} onChange={(e) => setQuery(e.target.value)} />
          <Btn variant="secondary" size="sm" type="submit">{id ? 'Cari' : 'Search'}</Btn>
        </form>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>{id ? 'Memuat…' : 'Loading…'}</div>
      ) : !data?.recipients?.length ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)', fontSize: 14 }}>
          {id ? 'Belum ada penerima sertifikat.' : 'No recipients yet.'}
        </div>
      ) : (
        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>{id ? 'Nama' : 'Full Name'}</th>
                <th>Email</th>
                <th>{id ? 'Tipe' : 'Template Type'}</th>
                <th>{id ? 'Training' : 'Training'}</th>
                <th>{id ? 'No. Identifikasi' : 'Identification No.'}</th>
                <th>{id ? 'Tgl Terbit' : 'Added On'}</th>
                <th>{id ? 'Berlaku Hingga' : 'Expiration Date'}</th>
                <th>{id ? 'Status Email' : 'Email Status'}</th>
              </tr>
            </thead>
            <tbody>
              {data.recipients.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 500 }}>{namaPenerima(r)}</td>
                  <td style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{emailPenerima(r)}</td>
                  <td><Badge tone="default">Certificate</Badge></td>
                  <td style={{ fontSize: 13 }}>{r.batch?.name ?? '—'}</td>
                  <td>
                    {r.certificate?.cert_no
                      ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {r.certificate.cert_no}
                          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 4, padding: '0 4px', fontSize: 11 }}
                            title={id ? 'Salin alamat kredensial' : 'Copy credential link'}
                            onClick={() => copyVerifyUrl(r.certificate)}>⎘</button>
                        </span>
                      : '—'}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{fmtDate(r.certificate?.issued_at)}</td>
                  <td style={{ fontSize: 13 }}>
                    {r.certificate?.expires_at ? fmtDate(r.certificate.expires_at) : (id ? 'Tanpa batas' : 'No expiry')}
                  </td>
                  <td>{recipientStatusBadge(r.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  )
}

// ─── Collections View ─────────────────────────────────────────────────────────

function CollectionFormModal({ collection, programId, onClose, onSaved }) {
  const { lang } = useApp()
  const id = lang === 'id'
  const [name, setName] = React.useState(collection?.name ?? '')
  const [description, setDescription] = React.useState(collection?.description ?? '')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')

  async function handleSave() {
    if (!name.trim()) { setError(id ? 'Nama wajib diisi.' : 'Name is required.'); return }
    setSaving(true)
    setError('')
    try {
      const payload = { name: name.trim(), description: description.trim() || null }
      const res = collection
        ? await updateCollection(collection.id, payload)
        : await createCollection(programId, payload)
      onSaved(res)
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{collection ? (id ? 'Edit Koleksi' : 'Edit Collection') : (id ? 'Buat Koleksi' : 'Create Collection')}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div className="alert alert-danger" style={{ padding: '8px 12px', fontSize: 13 }}>{error}</div>}
          <label className="field">
            <span className="field-label">{id ? 'Nama Koleksi' : 'Collection Name'} *</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder={id ? 'mis. IMO Nautical Level 1' : 'e.g. IMO Nautical Level 1'} />
          </label>
          <label className="field">
            <span className="field-label">{id ? 'Deskripsi' : 'Description'}</span>
            <textarea className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="secondary" onClick={onClose} disabled={saving}>{id ? 'Batal' : 'Cancel'}</Btn>
          <Btn variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (id ? 'Menyimpan…' : 'Saving…') : (id ? 'Simpan' : 'Save')}
          </Btn>
        </div>
      </div>
    </div>
  )
}

function CollectionsView({ programId, onChanged }) {
  const { lang } = useApp()
  const id = lang === 'id'
  const [collections, setCollections] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [editing, setEditing] = React.useState(null) // null | 'new' | collection
  const [error, setError] = React.useState('')

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      setCollections(await listCollections(programId))
    } finally {
      setLoading(false)
    }
  }, [programId])

  React.useEffect(() => { load() }, [load])

  function copyPublicUrl(slug) {
    navigator.clipboard.writeText(`${window.location.origin}/?collection=${encodeURIComponent(slug)}`).catch(() => {})
  }

  async function handleDelete(c) {
    if (!window.confirm(id ? `Hapus koleksi "${c.name}"? Batch yang terkait tidak akan terhapus, hanya dilepas dari koleksi.` : `Delete collection "${c.name}"? Linked batches are kept, just unassigned.`)) return
    try {
      await deleteCollection(c.id)
      await load()
      onChanged?.()
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h3 className="h3" style={{ margin: 0, flex: 1 }}>{id ? 'Koleksi Sertifikat' : 'Certificate Collections'}</h3>
        <Btn variant="primary" size="sm" onClick={() => setEditing('new')}>+ {id ? 'Buat Koleksi' : 'Create Collection'}</Btn>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 12, padding: '8px 12px', fontSize: 13 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>{id ? 'Memuat…' : 'Loading…'}</div>
      ) : collections.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)', fontSize: 14 }}>
          {id ? 'Belum ada koleksi. Kelompokkan training ke dalam koleksi bernama (mis. per level / klien).' : 'No collections yet. Group trainings into named collections.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {collections.map((c) => (
            <div key={c.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, background: 'var(--surface)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)', fontFamily: 'monospace', marginTop: 2 }}>/{c.slug}</div>
                </div>
              </div>
              {c.description && <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 8 }}>{c.description}</div>}
              <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
                <div><div style={{ fontSize: 18, fontWeight: 700 }}>{c.batchCount}</div><div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{id ? 'Batch' : 'Batches'}</div></div>
                <div><div style={{ fontSize: 18, fontWeight: 700 }}>{c.recipientCount}</div><div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{id ? 'Penerima' : 'Recipients'}</div></div>
                <div><div style={{ fontSize: 18, fontWeight: 700 }}>{c.sentCount}</div><div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>Sent</div></div>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => copyPublicUrl(c.slug)} title={id ? 'Salin link publik' : 'Copy public link'}>⎘ {id ? 'Link' : 'Link'}</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(c)}>{id ? 'Edit' : 'Edit'}</button>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', marginLeft: 'auto' }} onClick={() => handleDelete(c)}>{id ? 'Hapus' : 'Delete'}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CollectionFormModal
          collection={editing === 'new' ? null : editing}
          programId={programId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onChanged?.() }}
        />
      )}
    </div>
  )
}

// ─── Email Designer ───────────────────────────────────────────────────────────

const PLACEHOLDERS = [
  { key: 'recipient_name', id: 'Nama penerima', en: 'Recipient name' },
  { key: 'program_name', id: 'Nama program', en: 'Program name' },
  { key: 'cert_no', id: 'No. sertifikat', en: 'Certificate no.' },
  { key: 'code', id: 'Kode verifikasi', en: 'Verification code' },
  { key: 'expires_at', id: 'Tgl kadaluarsa kode', en: 'Code expiry' },
  { key: 'claim_url', id: 'Link klaim', en: 'Claim URL' },
  { key: 'org_name', id: 'Nama organisasi', en: 'Organisation name' },
]

// Sample values used only for the live preview.
const PREVIEW_VARS = {
  recipient_name: 'Budi Santoso',
  program_name: 'Nama Program',
  cert_no: 'LEMBAGA/PROGRAM/2026/ABC123',
  code: 'X7K9P2Q4',
  expires_at: '15/06/2026 17.00',
  claim_url: 'https://default.gladhy.co.id/?claim=1',
  org_name: 'Nama Lembaga',
}

function renderPreview(text = '') {
  return String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) =>
    Object.prototype.hasOwnProperty.call(PREVIEW_VARS, key) ? PREVIEW_VARS[key] : m
  )
}

function EmailDesigner({ programId }) {
  const { lang } = useApp()
  const id = lang === 'id'
  const [tpl, setTpl] = React.useState(null)
  const [isDefault, setIsDefault] = React.useState(true)
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg] = React.useState('')
  const [error, setError] = React.useState('')
  // Track which field the caret is in so "insert placeholder" targets it.
  const activeFieldRef = React.useRef('body')

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const res = await getEmailTemplate(programId)
      setTpl({ subject: res.subject, header: res.header, body: res.body, footer: res.footer })
      setIsDefault(res.is_default)
    } finally {
      setLoading(false)
    }
  }, [programId])

  React.useEffect(() => { load() }, [load])

  function setField(field, val) {
    setTpl((t) => ({ ...t, [field]: val }))
  }

  function insertPlaceholder(key) {
    const field = activeFieldRef.current
    setTpl((t) => ({ ...t, [field]: `${t[field] || ''}{{${key}}}` }))
  }

  async function handleSave() {
    setSaving(true)
    setError('')
    setMsg('')
    try {
      const res = await updateEmailTemplate(programId, tpl)
      setIsDefault(false)
      setMsg(id ? 'Template email tersimpan.' : 'Email template saved.')
      setTpl({ subject: res.subject, header: res.header, body: res.body, footer: res.footer })
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleReset() {
    if (!window.confirm(id ? 'Kembalikan ke template bawaan?' : 'Reset to default template?')) return
    setSaving(true)
    try {
      const res = await resetEmailTemplate(programId)
      setTpl({ subject: res.subject, header: res.header, body: res.body, footer: res.footer })
      setIsDefault(true)
      setMsg(id ? 'Direset ke template bawaan.' : 'Reset to default template.')
    } finally {
      setSaving(false)
    }
  }

  if (loading || !tpl) return <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>{id ? 'Memuat…' : 'Loading…'}</div>

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <h3 className="h3" style={{ margin: 0 }}>{id ? 'Desain Email Sertifikat' : 'Certificate Email Design'}</h3>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            {isDefault
              ? (id ? 'Saat ini memakai template bawaan. Simpan untuk mengkustomisasi.' : 'Currently using the default template. Save to customize.')
              : (id ? 'Template kustom aktif.' : 'Custom template active.')}
          </div>
        </div>
        {!isDefault && <Btn variant="secondary" size="sm" onClick={handleReset} disabled={saving}>{id ? 'Reset' : 'Reset'}</Btn>}
        <Btn variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? (id ? 'Menyimpan…' : 'Saving…') : (id ? 'Simpan' : 'Save')}
        </Btn>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 12, padding: '8px 12px', fontSize: 13 }}>{error}</div>}
      {msg && <div className="alert alert-success" style={{ marginBottom: 12, padding: '8px 12px', fontSize: 13 }}>{msg}</div>}

      {/* Placeholder chips */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 6 }}>
          {id ? 'Klik untuk menyisipkan ke field aktif:' : 'Click to insert into the active field:'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {PLACEHOLDERS.map((p) => (
            <button key={p.key} onClick={() => insertPlaceholder(p.key)}
              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'monospace' }}
              title={id ? p.id : p.en}>
              {`{{${p.key}}}`}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Editor */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label className="field">
            <span className="field-label">{id ? 'Subjek' : 'Subject'}</span>
            <input className="input" value={tpl.subject} onFocus={() => (activeFieldRef.current = 'subject')}
              onChange={(e) => setField('subject', e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">{id ? 'Header (judul)' : 'Header (title)'}</span>
            <input className="input" value={tpl.header} onFocus={() => (activeFieldRef.current = 'header')}
              onChange={(e) => setField('header', e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">{id ? 'Isi' : 'Body'}</span>
            <textarea className="input" rows={8} value={tpl.body} onFocus={() => (activeFieldRef.current = 'body')}
              onChange={(e) => setField('body', e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Footer</span>
            <textarea className="input" rows={3} value={tpl.footer} onFocus={() => (activeFieldRef.current = 'footer')}
              onChange={(e) => setField('footer', e.target.value)} />
          </label>
        </div>

        {/* Live preview */}
        <div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 6 }}>{id ? 'Pratinjau' : 'Preview'}</div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
            <div style={{ background: '#F8FAFC', padding: '8px 14px', borderBottom: '1px solid var(--border)', fontSize: 12, color: '#475569' }}>
              <strong>{id ? 'Subjek:' : 'Subject:'}</strong> {renderPreview(tpl.subject)}
            </div>
            <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif', color: '#0F172A' }}>
              {tpl.header && <h2 style={{ margin: '0 0 16px', fontSize: 20 }}>{renderPreview(tpl.header)}</h2>}
              <div style={{ lineHeight: 1.6, fontSize: 15, whiteSpace: 'pre-wrap' }}>{renderPreview(tpl.body)}</div>
              {!/\{\{\s*code\s*\}\}/.test(tpl.body) && (
                <div style={{ background: '#F1F5F9', padding: 16, borderRadius: 8, textAlign: 'center', fontFamily: 'monospace', fontSize: 22, letterSpacing: 2, margin: '16px 0' }}>
                  {PREVIEW_VARS.code}
                </div>
              )}
              <hr style={{ border: 'none', borderTop: '1px solid #E2E8F0', margin: '24px 0' }} />
              <p style={{ color: '#64748B', fontSize: 13, margin: 0, whiteSpace: 'pre-wrap' }}>{renderPreview(tpl.footer)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function CertBatchScreen({ embedded }) {
  const orgName = useOrgName()
  const { lang, currentProgram } = useApp()
  const id = lang === 'id'
  const programId = currentProgram?.id

  const [subTab, setSubTab] = React.useState('trainings') // 'trainings' | 'recipients' | 'email' | 'collections'
  const [view, setView] = React.useState('list') // 'list' | batchId string
  const [dashboard, setDashboard] = React.useState(null)
  const [trainers, setTrainers] = React.useState([])
  const [collections, setCollections] = React.useState([])
  const [classes, setClasses] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [creating, setCreating] = React.useState(false)
  const [error, setError] = React.useState('')

  const load = React.useCallback(async () => {
    if (!programId) return
    try {
      setLoading(true)
      const [dash, trainerList, collectionList, classList] = await Promise.all([
        listCertBatches(programId),
        listProgramTrainers(programId),
        listCollections(programId),
        listClasses(programId),
      ])
      setDashboard(dash)
      setTrainers(trainerList)
      setCollections(collectionList)
      setClasses(classList)
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setLoading(false)
    }
  }, [programId])

  React.useEffect(() => { load() }, [load])

  if (!programId) return (
    <div style={{ padding: 32, color: 'var(--fg-muted)', textAlign: 'center' }}>
      {id ? 'Pilih program terlebih dahulu.' : 'Please select a program first.'}
    </div>
  )

  // Batch detail — shown regardless of sub-tab
  if (view !== 'list') {
    return (
      <BatchDetail
        batchId={view}
        programId={programId}
        trainers={trainers}
        collections={collections}
        classes={classes}
        onBack={() => { setView('list'); load() }}
        onDeleted={() => { setView('list'); load() }}
      />
    )
  }

  return (
    <div>
      {!embedded && (
        <div className="page-header">
          <div className="page-header-text">
            <div className="eyebrow">{(currentProgram?.code || orgName).toUpperCase()} · {id ? 'Sertifikasi' : 'Credentials'}</div>
            <h1 className="h1" style={{ marginTop: 6 }}>{id ? 'Penerbitan Sertifikat' : 'Credential Issuance'}</h1>
            <div className="muted mt-1">
              {id ? 'Kelola batch penerbitan sertifikat peserta.' : 'Manage certificate issuance batches.'}
            </div>
          </div>
        </div>
      )}

      {/* Sub-tabs: Trainings | Recipients */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', marginBottom: 20, gap: 0 }}>
        {[
          { key: 'trainings', label: id ? 'Trainings' : 'Trainings' },
          { key: 'collections', label: id ? 'Koleksi' : 'Collections' },
          { key: 'recipients', label: id ? 'Penerima' : 'Recipients' },
          { key: 'email', label: id ? 'Email' : 'Email' },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setSubTab(key)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '8px 16px',
              fontSize: 14, fontWeight: subTab === key ? 600 : 400,
              color: subTab === key ? 'var(--brand)' : 'var(--fg-muted)',
              borderBottom: subTab === key ? '2px solid var(--brand)' : '2px solid transparent',
              marginBottom: -1,
            }}>
            {label}
          </button>
        ))}
        {subTab === 'trainings' && (
          <div style={{ marginLeft: 'auto' }}>
            <Btn variant="primary" onClick={() => setCreating(true)}>
              + {id ? 'Buat Training' : 'Create Training'}
            </Btn>
          </div>
        )}
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 12, padding: '8px 12px', fontSize: 13 }}>{error}</div>}

      {subTab === 'recipients' ? (
        <AllRecipientsView programId={programId} />
      ) : subTab === 'email' ? (
        <EmailDesigner programId={programId} />
      ) : subTab === 'collections' ? (
        <CollectionsView programId={programId} onChanged={load} />
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>{id ? 'Memuat…' : 'Loading…'}</div>
      ) : (
        <>
          <section style={{ marginBottom: 28 }}>
            <h3 className="h3" style={{ marginBottom: 10 }}>{id ? 'Pending' : 'Pending Trainings'}</h3>
            <BatchTable
              batches={dashboard?.pending}
              onSelect={setView}
              emptyMsg={id ? 'Tidak ada training yang tertunda.' : 'No pending trainings.'}
            />
          </section>
          <section>
            <h3 className="h3" style={{ marginBottom: 10 }}>{id ? 'Lebih Banyak' : 'More Trainings'}</h3>
            <BatchTable
              batches={dashboard?.more}
              onSelect={setView}
              emptyMsg={id ? 'Belum ada training yang selesai.' : 'No completed trainings yet.'}
            />
          </section>
        </>
      )}

      {creating && (
        <BatchFormModal
          programId={programId}
          trainers={trainers}
          collections={collections}
          classes={classes}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load() }}
        />
      )}
    </div>
  )
}
